import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, User, Settings, ListTodo, RefreshCw } from "lucide-react";
import { api, useFetch } from "@/lib/api";

const ACTION_ICONS = { created: ListTodo, updated: Settings, assigned: User, completed: ListTodo, paused: ListTodo, cancelled: ListTodo, status_changed: Settings, skill_updated: Settings, workload_adjusted: Settings, bulk_cancel: ListTodo, bulk_pause: ListTodo, bulk_assign: User };
const ENTITY_COLORS = { agent: "bg-neon-cyan/10 text-neon-cyan border-neon-cyan/20", task: "bg-neon-violet/10 text-neon-violet border-neon-violet/20" };

export default function AuditLog() {
  const [entityFilter, setEntityFilter] = useState("all");
  const [limit, setLimit] = useState(50);
  const { data: logs, loading, refetch } = useFetch(() => api.getAuditLogs({ entity_type: entityFilter === "all" ? undefined : entityFilter, limit }));

  const formatTime = (ts) => {
    if (!ts) return "-";
    const d = new Date(ts);
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider">AUDIT LOG</h1>
          <p className="text-sm text-muted-foreground mt-1">Track all system changes and actions</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-36 bg-white/5 border-white/10 rounded-sm text-sm" data-testid="audit-entity-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              <SelectItem value="agent">Agents</SelectItem>
              <SelectItem value="task">Tasks</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
            <SelectTrigger className="w-24 bg-white/5 border-white/10 rounded-sm text-sm" data-testid="audit-limit-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" onClick={refetch} data-testid="audit-refresh-btn">
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </div>

      <Card className="glass-panel hidden md:block">
        <CardContent className="p-0">
          <ScrollArea className="max-h-[70vh]">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-xs font-mono uppercase tracking-wider text-muted-foreground w-44">Timestamp</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Action</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Entity</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Name</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-wider text-muted-foreground">User</TableHead>
                    <TableHead className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && [1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i} className="border-white/5">
                      <TableCell colSpan={6}><div className="h-8 skeleton-shimmer rounded-sm" /></TableCell>
                    </TableRow>
                  ))}
                  {!loading && (logs || []).map((log) => {
                    const IconComp = ACTION_ICONS[log.action] || FileText;
                    const entityCls = ENTITY_COLORS[log.entity_type] || ENTITY_COLORS.task;
                    return (
                      <TableRow key={log.id} className="border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`audit-row-${log.id}`}>
                        <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">{formatTime(log.timestamp)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <IconComp className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                            <span className="text-xs font-mono">{log.action}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`${entityCls} text-[10px] font-mono uppercase tracking-wider rounded-sm px-2 py-0.5 border`}>
                            {log.entity_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs font-medium max-w-[150px] truncate">{log.entity_name || "-"}</TableCell>
                        <TableCell className="text-xs font-mono text-muted-foreground">{log.user}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{log.details}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!loading && (logs || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                        <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p className="font-mono text-sm">No audit logs found</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-2">
        {loading && [1, 2, 3, 4].map((i) => <div key={i} className="h-20 rounded-sm glass-panel skeleton-shimmer" />)}
        {!loading && (logs || []).map((log) => {
          const IconComp = ACTION_ICONS[log.action] || FileText;
          const entityCls = ENTITY_COLORS[log.entity_type] || ENTITY_COLORS.task;
          return (
            <Card key={log.id} className="glass-panel" data-testid={`audit-mobile-${log.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-sm bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <IconComp className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono font-medium">{log.action}</span>
                      <Badge variant="outline" className={`${entityCls} text-[10px] font-mono uppercase tracking-wider rounded-sm px-1.5 py-0 border`}>{log.entity_type}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">{log.entity_name || log.details}</p>
                    <div className="flex items-center justify-between mt-1.5 text-[10px] font-mono text-muted-foreground">
                      <span>{log.user}</span>
                      <span>{formatTime(log.timestamp)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {!loading && (logs || []).length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="font-mono text-sm">No audit logs found</p>
          </div>
        )}
      </div>
    </div>
  );
}
